import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { supabase } from '@/lib/supabaseClient';
import { ICONS, Icon, InfoShell } from './InfoShell';

// New-design Shipping & Returns (/shipping-returns). Delivery charges come
// from core_shipping_rates (the same table checkout uses), as on the classic
// page; every text is the classic page's i18n key.

export default function ShippingV2() {
  const { t } = useTranslation();
  const [rates, setRates] = useState({ dhaka: 60, outside: 120, freeThreshold: 3000 });

  useEffect(() => {
    let on = true;
    supabase
      .from('core_shipping_rates')
      .select('dhaka_charge_bdt, outside_dhaka_charge_bdt, free_shipping_threshold_bdt')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data }) => {
        if (on && data) setRates({ dhaka: data.dhaka_charge_bdt, outside: data.outside_dhaka_charge_bdt, freeThreshold: data.free_shipping_threshold_bdt });
      });
    return () => {
      on = false;
    };
  }, []);

  const dhaka = `৳${rates.dhaka}`;
  const outside = `৳${rates.outside}`;
  const free = `৳${rates.freeThreshold.toLocaleString()}`;

  const options = [
    { icon: ICONS.truck, title: t('shipping.dhaka.title'), price: dhaka, time: t('shipping.dhaka.time'), desc: t('shipping.dhaka.desc', { price: dhaka }) },
    { icon: ICONS.truck, title: t('shipping.outside.title'), price: outside, time: t('shipping.outside.time'), desc: t('shipping.outside.desc', { price: outside }) },
    {
      icon: ICONS.gift,
      title: t('shipping.free.title'),
      price: '৳0',
      time: t('infoV2.shipping.over', 'Orders over {{threshold}}', { threshold: free }),
      desc: t('shipping.free.desc', { threshold: free }),
      hi: true,
    },
  ];
  const steps = [1, 2, 3].map((n) => ({ n, title: t(`shipping.step${n}.title`), desc: t(`shipping.step${n}.desc`) }));
  const ok = [1, 2, 3].map((n) => t(`shipping.condition${n}`));
  const no = [4, 5].map((n) => t(`shipping.condition${n}`));

  return (
    <>
      <Seo pageKey="shipping" />
      <InfoShell
        label={t('shipping.hero.label')}
        title={t('shipping.hero.heading')}
        lede={t('shipping.hero.subtitle')}
        meta={
          <dl className="cstat">
            <div>
              <dt>{dhaka}</dt>
              <dd>{t('infoV2.shipping.inDhaka', 'Inside Dhaka')}</dd>
            </div>
            <div>
              <dt>{outside}</dt>
              <dd>{t('infoV2.shipping.outDhaka', 'Outside Dhaka')}</dd>
            </div>
            <div>
              <dt>{free}+</dt>
              <dd>{t('infoV2.shipping.freeLabel', 'Free delivery')}</dd>
            </div>
          </dl>
        }
      >
        {/* Delivery */}
        <section className="isec">
          <div className="wrap">
            <div className="ihd rv">
              <span className="lbl">{t('infoV2.shipping.delivery', 'Delivery')}</span>
              <h2>{t('shipping.options.heading')}</h2>
            </div>
            <div className="sopt">
              {options.map((o, i) => (
                <article key={i} className={`rv${o.hi ? ' hi' : ''}`} style={{ ['--d' as string]: `${i * 0.08}s` }}>
                  <div className="top">
                    <span className="ic">
                      <Icon d={o.icon} />
                    </span>
                    <span className="tm">{o.time}</span>
                  </div>
                  <h3>{o.title}</h3>
                  <b className="pr">{o.price}</b>
                  <p>{o.desc}</p>
                </article>
              ))}
            </div>
            <p className="snote rv">
              <Icon d={ICONS.phone} />
              {t('shipping.track.desc', 'All orders include tracking via SMS and email updates.')}
            </p>
          </div>
        </section>

        {/* Returns */}
        <section className="isec soft">
          <div className="wrap">
            <div className="ihd rv">
              <span className="lbl">{t('infoV2.shipping.returns', 'Returns')}</span>
              <h2>{t('shipping.returns.heading')}</h2>
              <p>{t('shipping.returns.desc')}</p>
            </div>
            <ol className="ssteps">
              {steps.map((s, i) => (
                <li key={s.n} className="rv" style={{ ['--d' as string]: `${i * 0.1}s` }}>
                  <span className="sn">{String(s.n).padStart(2, '0')}</span>
                  <small>
                    {t('common.step')} {s.n}
                  </small>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </li>
              ))}
            </ol>

            <div className="scond rv">
              <h3>{t('shipping.conditions.heading')}</h3>
              <div className="cols">
                <ul className="yes">
                  {ok.map((c) => (
                    <li key={c}>
                      <i>
                        <Icon d={ICONS.check} />
                      </i>
                      {c}
                    </li>
                  ))}
                </ul>
                <ul className="no">
                  {no.map((c) => (
                    <li key={c}>
                      <i>
                        <Icon d={ICONS.close} />
                      </i>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="sinfo">
              <article className="rv">
                <span className="ic">
                  <Icon d={ICONS.return} />
                </span>
                <h3>{t('shipping.refundTimeline.heading', 'Refund Timeline')}</h3>
                <p>
                  {t(
                    'shipping.refundTimeline.desc',
                    'Once your returned item is received and inspected, approved refunds are issued within 7–10 working days to your original payment method (or via bKash/bank transfer for Cash on Delivery orders). You will receive an email confirmation once your refund has been processed.',
                  )}
                </p>
              </article>
              <article className="rv" style={{ ['--d' as string]: '.08s' }}>
                <span className="ic">
                  <Icon d={ICONS.close} />
                </span>
                <h3>{t('shipping.cancellation.heading', 'Order Cancellation')}</h3>
                <p>
                  {t(
                    'shipping.cancellation.desc',
                    'You may cancel your order free of charge at any time before it has been dispatched, by contacting support@rxkseoul.com or WhatsApp with your order number. Once an order has been dispatched for delivery, it can no longer be cancelled — our standard Return Policy above applies instead after delivery.',
                  )}
                </p>
              </article>
            </div>
          </div>
        </section>
      </InfoShell>
    </>
  );
}
